import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import MiniBotWidget from './MiniBotWidget';
import {
  ChevronLeft, BookOpen, CheckCircle, Circle, Lock,
  Loader2, Award, X, ShieldAlert, Video, VideoOff,
  Play, Square,
  Eye, Headphones, Type, Zap, FileText,
  ChevronDown, ChevronRight, ArrowRight
} from 'lucide-react';
import DOMPurify from 'dompurify';
/* ─── COLORES DE MARCA ────────────────────────────────────────────────────────
   Todo sale de las variables que define ThemeProvider:
     primary   → --brand-primary / --brand-primary-rgb    (antes cian)
     secondary → --brand-secondary / --brand-secondary-rgb (antes magenta)
     accent    → --brand-accent / --brand-accent-rgb       (antes lima)
   Los valores tras la coma son los fallbacks cuando no hay organización.
──────────────────────────────────────────────────────────────────────────── */
const cP = 'var(--brand-primary, #00FFFF)';
const cS = 'var(--brand-secondary, #FF00FF)';
const cA = 'var(--brand-accent, #ccff00)';

const P = (o = 1) => `rgb(var(--brand-primary-rgb, 0 255 255) / ${o})`;
const S = (o = 1) => `rgb(var(--brand-secondary-rgb, 255 0 255) / ${o})`;
const A = (o = 1) => `rgb(var(--brand-accent-rgb, 204 255 0) / ${o})`;

// Colores semánticos (no son de marca): avisos y errores
const ORANGE = (o = 1) => `rgba(249,115,22,${o})`;
const RED    = (o = 1) => `rgba(239,68,68,${o})`;

/* ─── GLOBAL STYLES ──────────────────────────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .dash-root {
    font-family: var(--org-font, 'DM Sans', sans-serif);
    min-height: 100vh;
    background-color: var(--brand-bg, #060810);
    background-image: var(--brand-bg-image, url('/bg-dashboard.png'));
    background-size: cover;
    background-position: center top;
    background-attachment: fixed;
    position: relative;
    overflow-x: hidden;
    animation: fadeIn 0.4s ease;
    color: #ffffff;
  }

  .test-option:has(input:focus-visible) {
    outline: 2px solid var(--brand-accent, #ccff00);
    outline-offset: 2px;
  }

  .dash-root::before {
    content: ''; position: fixed; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, var(--org-scan, rgb(var(--brand-primary-rgb, 0 255 255) / 0.4)), transparent);
    animation: scanLine 8s linear infinite; z-index: 1; pointer-events: none;
  }

  .main-content {
    position: relative; z-index: 2; padding: 32px 20px; max-width: 64rem; margin: 0 auto;
  }

  .glass-panel {
    background: rgba(10, 8, 30, 0.65);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 0.5px rgba(255,255,255,0.05);
  }

  /* ── GLOW EFFECTS ── */
  .glow-cyan {
    box-shadow: 0 0 20px rgb(var(--brand-primary-rgb, 0 255 255) / 0.12);
    border-color: rgb(var(--brand-primary-rgb, 0 255 255) / 0.3);
  }
  .glow-magenta {
    box-shadow: 0 0 20px rgb(var(--brand-secondary-rgb, 255 0 255) / 0.12);
    border-color: rgb(var(--brand-secondary-rgb, 255 0 255) / 0.3);
  }
  .glow-lime {
    box-shadow: 0 0 20px rgb(var(--brand-accent-rgb, 204 255 0) / 0.12);
    border-color: rgb(var(--brand-accent-rgb, 204 255 0) / 0.3);
  }

  /* ── NEON BUTTONS ── */
  .btn-neon-cyan {
    background: var(--brand-primary, #00FFFF); color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgb(var(--brand-primary-rgb, 0 255 255) / 0.5); transition: all 0.2s;
  }
  .btn-neon-cyan:hover:not(:disabled) {
    filter: brightness(1.15);
    box-shadow: 0 0 25px rgb(var(--brand-primary-rgb, 0 255 255) / 0.75);
    transform: translateY(-1px);
  }
  .btn-neon-cyan:disabled {
    background: rgb(var(--brand-primary-rgb, 0 255 255) / 0.12);
    color: rgb(var(--brand-primary-rgb, 0 255 255) / 0.35);
    box-shadow: none; cursor: not-allowed;
  }

  .btn-neon-magenta {
    background: var(--brand-secondary, #FF00FF); color: #fff; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgb(var(--brand-secondary-rgb, 255 0 255) / 0.5); transition: all 0.2s;
  }
  .btn-neon-magenta:hover:not(:disabled) {
    filter: brightness(1.15);
    box-shadow: 0 0 25px rgb(var(--brand-secondary-rgb, 255 0 255) / 0.75);
    transform: translateY(-1px);
  }
  .btn-neon-magenta:disabled {
    background: rgb(var(--brand-secondary-rgb, 255 0 255) / 0.12);
    color: rgba(255, 255, 255, 0.3);
    box-shadow: none; cursor: not-allowed;
  }

  .btn-neon-lime {
    background: var(--brand-accent, #ccff00); color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgb(var(--brand-accent-rgb, 204 255 0) / 0.5); transition: all 0.2s;
  }
  .btn-neon-lime:hover:not(:disabled) {
    filter: brightness(1.12);
    box-shadow: 0 0 25px rgb(var(--brand-accent-rgb, 204 255 0) / 0.75);
    transform: translateY(-1px);
  }
  .btn-neon-lime:disabled {
    background: rgb(var(--brand-accent-rgb, 204 255 0) / 0.12);
    color: rgb(var(--brand-accent-rgb, 204 255 0) / 0.35);
    box-shadow: none; cursor: not-allowed;
  }

  /* ── MARKDOWN ── */
  .markdown-content h1 { color: #fff; font-size: 1.75rem; font-weight: 900; margin-top: 1.5rem; margin-bottom: 1rem; }
  .markdown-content h2 { color: #fff; font-size: 1.5rem; font-weight: 800; margin-top: 2rem; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid rgb(var(--brand-primary-rgb, 0 255 255) / 0.3); }
  .markdown-content h3 { color: var(--brand-primary, #00FFFF); font-size: 1.25rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.75rem; }
  .markdown-content p { color: rgba(255,255,255,0.8); line-height: 1.7; margin-bottom: 1rem; }
  .markdown-content ul { list-style-type: disc; margin-left: 1.5rem; margin-bottom: 1rem; color: rgba(255,255,255,0.8); }
  .markdown-content li { margin-bottom: 0.5rem; }
  .markdown-content strong { color: var(--brand-accent, #ccff00); font-weight: 700; }
  .markdown-content em { color: var(--brand-secondary, #FF00FF); }

  ::-webkit-scrollbar { width: 8px; }
  ::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: rgb(var(--brand-primary-rgb, 0 255 255) / 0.35); }
`;

// Tonos de marca para insignias: p = primary, s = secondary, a = accent, o = naranja (semántico)
const toneStyle = (tone) => {
  if (tone === 'p') return { background: P(0.1), color: cP, borderColor: P(0.3), boxShadow: `0 0 10px ${P(0.15)}` };
  if (tone === 's') return { background: S(0.1), color: cS, borderColor: S(0.3), boxShadow: `0 0 10px ${S(0.15)}` };
  if (tone === 'a') return { background: A(0.1), color: cA, borderColor: A(0.3), boxShadow: `0 0 10px ${A(0.15)}` };
  return { background: ORANGE(0.1), color: '#fb923c', borderColor: ORANGE(0.3), boxShadow: `0 0 10px ${ORANGE(0.15)}` };
};

const STYLE_CONFIG = {
  visual:      { label: 'Visual',      icon: Eye,        tone: 'p' },
  auditory:    { label: 'Auditivo',    icon: Headphones, tone: 's' },
  reading:     { label: 'Lectura',     icon: Type,       tone: 'a' },
  kinesthetic: { label: 'Kinestésico', icon: Zap,        tone: 'o' },
};

// Estilos de aprendizaje que se aceptan al leer el usuario guardado
const VALID_LEARNING_STYLES = new Set(['auditory', 'reading', 'kinesthetic']);

// Estado visual de la insignia de evaluación
const EVAL_STATUS = {
  passed: {
    style: { background: A(0.1), color: cA, borderColor: A(0.3), boxShadow: `0 0 10px ${A(0.1)}` },
    label: 'Evaluación Superada',
  },
  ready: {
    style: { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)' },
    label: 'Evaluación Operativa',
  },
  locked: {
    style: { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.1)' },
    label: 'Evaluación Bloqueada',
  },
};

const HEADINGS = [
  { re: /^###\s/, Tag: 'h3', cut: 4 },
  { re: /^##\s/,  Tag: 'h2', cut: 3 },
  { re: /^#\s/,   Tag: 'h1', cut: 2 },
];

// ── Lee el learning style guardado en localStorage; null si no hay uno válido ──
const readStoredLearningStyle = () => {
  try {
    const stored = localStorage.getItem('user');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (VALID_LEARNING_STYLES.has(parsed.learning_style)) return parsed.learning_style;
    }
  } catch {
    // localStorage no disponible o JSON corrupto: se ignora
  }
  return null;
};

// ── Hook: obtener learning style actualizado ──
const useLearningStyle = (user) => {
  const getInitial = () => {
    const stored = readStoredLearningStyle();
    if (stored) return stored;
    return VALID_LEARNING_STYLES.has(user?.learning_style) ? user.learning_style : 'auditory';
  };
  const [style, setStyle] = useState(getInitial);

  useEffect(() => {
    if (VALID_LEARNING_STYLES.has(user?.learning_style)) setStyle(user.learning_style);
  }, [user?.learning_style]);

  useEffect(() => {
    const id = setInterval(() => {
      const stored = readStoredLearningStyle();
      if (stored) setStyle(prev => (stored === prev ? prev : stored));
    }, 1500);
    return () => clearInterval(id);
  }, []);

  return style;
};

const inlineMarkdownToHtml = (line) => DOMPurify.sanitize(
  line
    .replaceAll(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replaceAll(/\*(.+?)\*/g, '<em>$1</em>'),
  { ALLOWED_TAGS: ['strong', 'em'], ALLOWED_ATTR: [] }
);

const MarkdownContent = ({ content }) => {
  if (!content) return <p className="text-white/50 italic text-sm uppercase tracking-widest font-bold">Sin contenido disponible.</p>;

  const els = [];
  let listItems = [];
  let counter = 0;
  const nextKey = () => counter++;

  const addListItem = (text) => listItems.push({ id: nextKey(), text });

  const flush = () => {
    if (!listItems.length) return;
    const items = listItems;
    listItems = [];
    els.push(
      <ul key={nextKey()}>
        {items.map(item => <li key={item.id}>{item.text}</li>)}
      </ul>
    );
  };

  content.split('\n').forEach(line => {
    const heading = HEADINGS.find(h => h.re.test(line));
    if (heading) {
      flush();
      const { Tag } = heading;
      els.push(<Tag key={nextKey()}>{line.slice(heading.cut)}</Tag>);
      return;
    }
    if (/^\d+\.\s/.test(line)) {
      addListItem(line.replaceAll(/^\d+\.\s/g, ''));
      return;
    }
    if (/^[-*•]\s/.test(line)) {
      addListItem(line.slice(2));
      return;
    }

    flush();
    if (line.trim()) {
      els.push(
  <p key={nextKey()} dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(line) }} />
);
    } else {
      els.push(<div key={nextKey()} className="h-3" />);
    }
  });
  flush();

  return <div className="markdown-content">{els}</div>;
};

const cleanForSpeech = (content) => content
  .replaceAll(/#{1,6}\s/g, '')
  .replaceAll(/\*\*(.+?)\*\*/g, '$1')
  .replaceAll(/\*(.+?)\*/g, '$1')
  .replaceAll(/^[-*•]\s/gm, '')
  .slice(0, 5000);

const TTSBar = ({ content }) => {
  const [speaking, setSpeaking] = useState(false);
  const resumeIntervalRef = useRef(null);
  const supported = 'speechSynthesis' in globalThis;

  useEffect(() => {
    if (!supported) return;
    // Chrome carga las voces de forma asíncrona: pedirlas aquí las precarga
    globalThis.speechSynthesis.getVoices();
    return () => {
      globalThis.speechSynthesis?.cancel();
      clearInterval(resumeIntervalRef.current);
    };
  }, [supported]);

  const speak = () => {
    if (!content?.trim()) {
      toast.error('No hay texto disponible para reproducir en esta unidad');
      return;
    }
    globalThis.speechSynthesis.cancel();
    clearInterval(resumeIntervalRef.current);

    const u = new SpeechSynthesisUtterance(cleanForSpeech(content));
    u.lang = 'es-ES';
    u.rate = 0.92;

    const esV = globalThis.speechSynthesis.getVoices().find(v => v.lang.startsWith('es'));
    if (esV) u.voice = esV;

    u.onstart = () => {
      setSpeaking(true);
      // Workaround bug Chrome: forzar resume cada 10s para que no se corte
      resumeIntervalRef.current = setInterval(() => {
        if (globalThis.speechSynthesis.speaking) globalThis.speechSynthesis.resume();
      }, 10000);
    };
    u.onend = () => { setSpeaking(false); clearInterval(resumeIntervalRef.current); };
    u.onerror = (e) => {
      console.error('TTS error:', e);
      setSpeaking(false);
      clearInterval(resumeIntervalRef.current);
      toast.error('Error al reproducir el audio');
    };

    // Pequeño delay evita el bug de "speak() silencioso" en Chrome
    setTimeout(() => globalThis.speechSynthesis.speak(u), 50);
  };

  const stop = () => {
    globalThis.speechSynthesis.cancel();
    clearInterval(resumeIntervalRef.current);
    setSpeaking(false);
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl mb-6"
      style={{ background: S(0.05), border: `1px solid ${S(0.3)}`, boxShadow: `inset 0 0 15px ${S(0.05)}` }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ background: S(0.2), border: `1px solid ${S(0.4)}`, color: cS }}>
        <Headphones className="w-4 h-4 flex-shrink-0" />
      </div>
      <span className="text-[11px] uppercase tracking-widest font-black flex-1" style={{ color: cS }}>Emisión Neuronal (Audio)</span>
      {speaking
        ? <button onClick={stop} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-colors"
            style={{ background: RED(0.2), color: '#f87171', border: `1px solid ${RED(0.4)}` }}>
            <Square className="w-3.5 h-3.5" />Detener
          </button>
        : <button onClick={speak} className="btn-neon-magenta flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-widest">
            <Play className="w-3.5 h-3.5" />Reproducir
          </button>
      }
    </div>
  );
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const randomizeTest = (t) => ({
  ...t,
  questions: shuffle(t.questions).map(q => ({ ...q, options: shuffle(q.options) })),
});



const ANTI_CSS = `.test-zone,.test-zone *{user-select:none!important;-webkit-user-select:none!important;}@media print{.test-zone{visibility:hidden!important;} body::after { content: 'Acceso Denegado. Contenido Protegido.'; display: block; font-size: 20px; text-align: center; padding: 60px; color: white; background: #060810; }}`;



const useAdaptedContent = (content, learningStyle, subModuleId, api) => {
  const [adapted, setAdapted] = useState(null);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef({});

  useEffect(() => {
    if (!content || learningStyle === 'visual') { setAdapted(null); return; }
    const key = `${subModuleId}__${learningStyle}`;
    if (cacheRef.current[key]) { setAdapted(cacheRef.current[key]); return; }
    let cancelled = false;
    setLoading(true); setAdapted(null);

    api.post('/ai/generate', {
      task: 'adapt',
      style: learningStyle,
      title: '',
      content: content.slice(0, 3000),
    })
      .then(res => { if (!cancelled && res.data.response) { cacheRef.current[key] = res.data.response; setAdapted(res.data.response); } })
      .catch(err => console.warn('No se pudo adaptar el contenido; se muestra el original:', err))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; setLoading(false); };
  }, [content, learningStyle, subModuleId, api]);

  return { adapted, loading };
};

const getScormOrigin = () => {
  try {
    return new URL(process.env.REACT_APP_API_URL).origin;
  } catch {
    return null;
  }
};

const getScormSrc = (url) => {
  if (!url) return '';

  if (/^https?:\/\//i.test(url )) {
    return url;
  }

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;

  return `${apiBase}${path}`;
};

const SubModuleViewer = () => {
  const { moduleId, subModuleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user } = useAuth();
  const learningStyle = useLearningStyle(user);
    // ── Cartel "Pasamos a la Unidad X" ──
  const transitionRef = useRef(null);
  const scormIframeRef = useRef(null);
  if (location.state?.transitionTo) transitionRef.current = location.state.transitionTo;
  const transitionInfo = transitionRef.current;
  const [minElapsed, setMinElapsed] = useState(!transitionInfo);

  useEffect(() => {
    if (!transitionRef.current) { setMinElapsed(true); return; }
    setMinElapsed(false);
    const t = setTimeout(() => {
      setMinElapsed(true);
      transitionRef.current = null;
    }, 1800);
    return () => clearTimeout(t);
  }, [subModuleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [subModule, setSubModule]       = useState(null);
  const [parentModule, setParentModule] = useState(null);
  const [progress, setProgress]         = useState({ sections_completed:[], test_passed:false, overall_progress:0 });
  const [loading, setLoading]           = useState(true);
  const [test, setTest]                 = useState(null);
  const [activeTest, setActiveTest]     = useState(null);
  const [testAnswers, setTestAnswers]   = useState({});
  const [submitting, setSubmitting]     = useState(false);
  const [violations, setViolations]     = useState(0);
  const [marking, setMarking]           = useState(false);
  const [showStart, setShowStart]       = useState(false);
  const [recording, setRecording]       = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [allSubModules, setAllSubModules] = useState([]);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const streamRef = useRef(null);
  const loadedIdRef = useRef(null);
  const scormPassedRef = useRef(false);
  // ── TIME TRACKING ──
  const sessionStartRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const { adapted: adaptedContent, loading: adapting } = useAdaptedContent(subModule?.content, learningStyle, subModuleId, api);

  useEffect(() => {
    if (!subModule || !moduleId) return;

    sessionStartRef.current = Date.now();
    let totalSent = 0;

    timeIntervalRef.current = setInterval(async () => {
      try {
        await api.post(`/modules/${moduleId}/track-time`, {
          module_id: moduleId,
          session_seconds: 30,
        });
        totalSent += 30;
      } catch (e) {
        console.warn('Time tracking error:', e);
      }
    }, 30000);

    return () => {
      clearInterval(timeIntervalRef.current);
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      const remaining = elapsed - totalSent;
      if (remaining > 5) {
        api.post(`/modules/${moduleId}/track-time`, {
          module_id: moduleId,
          session_seconds: remaining,
        }).catch(err => console.warn('No se pudo enviar el tiempo restante de la sesión:', err));
      }
    };
  }, [subModule, moduleId, api]);

  const [moduleFlags, setModuleFlags] = useState({
    require_screen_recording: false,
    require_time_tracking: false,
  });
  const scormStateRef = useRef({});

  useEffect(() => {
    scormPassedRef.current = false; // reset al cambiar de unidad
    scormStateRef.current = {};
  }, [subModuleId]);

  useEffect(() => {
    const handleScormMessage = async (event) => {
  // 1. Solo mensajes del origen del backend (donde se sirve el SCORM)
      if (event.origin !== getScormOrigin()) return;
      // 2. Solo si vienen de NUESTRO iframe, no de otra ventana
      if (event.source !== scormIframeRef.current?.contentWindow) return;

      const { type, key, value } = event.data || {};

      if (type !== 'scorm_event') return;

      scormStateRef.current[key] = value; // acumular todo lo que llega
      console.log('SCORM event:', key, value, scormStateRef.current);

      const isRecordingTrigger = key === 'cmi.core.lesson_location' || key === 'cmi.interactions.0.id';
      if (moduleFlags.require_screen_recording && isRecordingTrigger && !recording) {
        toast.info('📹 El test requiere grabación de pantalla');
        await startRecording();
      }

      if (isScormPassed(scormStateRef.current) && !progress.test_passed && !scormPassedRef.current) {
        scormPassedRef.current = true;
        await new Promise(r => setTimeout(r, 1500));
        await handleScormTestPassed();
      }
    };

    globalThis.addEventListener('message', handleScormMessage);
    return () => globalThis.removeEventListener('message', handleScormMessage);
  }, [moduleFlags.require_screen_recording, progress.test_passed, allSubModules, subModuleId]);

  // ── NAVEGACIÓN TRAS APROBAR (compartida entre test SCORM y test de plataforma) ──
  const getNextUnit = () => {
    const sortedUnits = [...allSubModules].sort((a, b) => a.unit_number - b.unit_number);
    const currentIndex = sortedUnits.findIndex(sm => sm.id === subModuleId);
    if (currentIndex === -1) return null;
    return sortedUnits[currentIndex + 1] ?? null;
  };

  const goToNextUnit = (nextUnit) => {
    setTimeout(() => {
      navigate(`/module/${moduleId}/unit/${nextUnit.id}`, {
        replace: true,
        state: {
          autostart: true,
          transitionTo: { number: nextUnit.unit_number, title: nextUnit.title },
        },
      });
    }, 800);
  };

  const goToModule = (options) => {
    setTimeout(() => navigate(`/module/${moduleId}`, options), 1500);
  };

  // Nota real (0-100) que reporta el SCORM, o null si no reporta ninguna
  const getScormScorePct = (s) => {
    const raw = s['cmi.core.score.raw'] ?? s['cmi.score.raw'];
    const max = s['cmi.core.score.max'] ?? s['cmi.score.max'];
    const scaled = s['cmi.score.scaled'];
    const r = Number.parseFloat(raw);
    if (raw !== undefined && raw !== '' && !Number.isNaN(r)) {
      const m = Number.parseFloat(max);
      return !Number.isNaN(m) && m > 0 ? (r / m) * 100 : r;
    }
    const sc = Number.parseFloat(scaled);
    if (scaled !== undefined && scaled !== '' && !Number.isNaN(sc)) return sc * 100;
    return null;
  };

  // "passed" explícito manda; "completed" solo vale si la nota (si existe) llega al umbral
  const isScormPassed = (s, threshold = 70) => {
    const explicit = s['cmi.core.lesson_status'] === 'passed' || s['cmi.success_status'] === 'passed';
    const failed = s['cmi.core.lesson_status'] === 'failed' || s['cmi.success_status'] === 'failed';
    if (failed) return false;
    if (explicit) return true;
    const completed = s['cmi.core.lesson_status'] === 'completed' || s['cmi.completion_status'] === 'completed';
    if (!completed) return false;
    const pct = getScormScorePct(s);
    return pct === null || pct >= threshold;
  };

  const handleScormTestPassed = async () => {
    try {
      stopRecording();
      const pct = getScormScorePct(scormStateRef.current);   // puede ser null
      const res = await api.post(`/submodules/${subModuleId}/test-results`, {
        test_id: 'scorm-native',
        answers: {},
        score: pct ?? 0,
        max_score: 100,
        percentage: pct,          // null si el SCORM no manda nota
        passed: true,
        violations: 0,
        screen_recorded: recording,
      });
      await load();
      toast.success('¡Evaluación SCORM superada!');

      const nextUnit = getNextUnit();
      if (nextUnit) {
        goToNextUnit(nextUnit);
      } else if (res.data.module_completed) {
        toast.success('🏆 ¡MÓDULO COMPLETADO!');
        goToModule();
      } else {
        goToModule({ state: { openRoleplay: true } });
      }
    } catch {
      toast.error('Error al registrar la evaluación SCORM');
      scormPassedRef.current = false; // permite reintentar si falló el guardado
    }
  };

  const cfg = STYLE_CONFIG[learningStyle] || STYLE_CONFIG.visual;

  const StyleIcon = cfg.icon;

  const load = useCallback(async () => {
    if (loadedIdRef.current !== subModuleId) setLoading(true);
    try {
      const [smRes, pmRes, progRes, allSmRes] = await Promise.all([
        api.get(`/submodules/${subModuleId}`),
        api.get(`/modules/${moduleId}`),
        api.get(`/submodules/${subModuleId}/my-progress`),
        api.get(`/modules/${moduleId}/submodules`)
      ]);
      setSubModule(smRes.data);
      setParentModule(pmRes.data);
      setAllSubModules(allSmRes.data || []);
      console.log('MODULE FLAGS RAW:', {
        requires_screen_record: pmRes.data.requires_screen_record,
        require_screen_recording: pmRes.data.require_screen_recording,
      });
      const flags = {
        require_screen_recording: !!(pmRes.data.requires_screen_record || pmRes.data.require_screen_recording),
        require_time_tracking: !!pmRes.data.require_time_tracking,
      };
      setModuleFlags(flags);
      console.log('FLAGS CALCULADOS:', flags);

      setProgress(progRes.data || { sections_completed:[], test_passed:false, overall_progress:0 });
      try {
        const tRes = await api.get(`/submodules/${subModuleId}/test`);
        setTest(tRes.data);
      } catch {
        // La unidad puede no tener test todavía: se sigue sin él
      }
      // Contar intentos usados y sincronizar pass_threshold
      try {
        const resultsRes = await api.get(`/submodules/${subModuleId}/test-results-count`);
        setAttemptsUsed(resultsRes.data.count || 0);
        const serverMaxAttempts = resultsRes.data.max_attempts ?? 3;
        const serverPassThreshold = resultsRes.data.pass_threshold ?? 70;
        setTest(prev => (prev
          ? { ...prev, pass_threshold: serverPassThreshold, max_attempts: serverMaxAttempts }
          : { pass_threshold: serverPassThreshold, max_attempts: serverMaxAttempts }
        ));
      } catch {
        setAttemptsUsed(0);
      }
    } catch { toast.error('Error al cargar la unidad'); }
    finally {
      loadedIdRef.current = subModuleId;
      setLoading(false);
    }
  }, [api, moduleId, subModuleId]);

  useEffect(() => { load(); }, [load]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video:{ frameRate:5 }, audio:false });
      streamRef.current = stream;
      stream.getVideoTracks()[0].onended = () => { setRecording(false); toast.error('⚠️ Transmisión de pantalla interrumpida'); };
      setRecording(true);
      toast.success('🔴 Monitorización Iniciada', { duration:3000 });
      return true;
    } catch {
      toast.error('❌ Debes permitir compartir pantalla para realizar este test');
      return false;
    }
  };

  const stopRecording = () => { streamRef.current?.getTracks().forEach(t=>t.stop()); setRecording(false); };

  useEffect(() => {
    if (!activeTest) return;

    const warn = (msg) => {
      setViolations(v => v + 1);
      toast.error(msg, { duration: 3000 });
    };

    const styleEl = document.createElement('style');
    styleEl.id = 'sm-ac';
    styleEl.innerHTML = ANTI_CSS;
    document.head.appendChild(styleEl);

    const onCtx = (e) => {
      e.preventDefault();
      warn('⚠️ Protocolo: Clic derecho bloqueado');
    };
    const onKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && ['c', 'a', 'p', 'v'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        warn('⚠️ Protocolo: Atajo de teclado bloqueado');
      }
    };
    const onCopy = (e) => {
      e.preventDefault();
      warn('⚠️ Protocolo: Extracción de datos bloqueada');
    };
    const onVis = () => {
      if (document.hidden) warn('⚠️ Protocolo: Cambio de ventana detectado');
    };

    document.addEventListener('contextmenu', onCtx);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('copy', onCopy);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('contextmenu', onCtx);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('visibilitychange', onVis);
      document.getElementById('sm-ac')?.remove();
    };
  }, [activeTest]);

  const markSectionComplete = async (sectionId) => {
    try {
      const res = await api.post(`/submodules/${subModuleId}/mark-section-complete`, { section_id: sectionId });
      setProgress(p => ({
        ...p,
        sections_completed: res.data.sections_completed || [...(p.sections_completed||[]), sectionId],
        overall_progress: res.data.overall_progress ?? p.overall_progress,
      }));
      toast.success('Fragmento asimilado');
      const newCompleted = res.data.sections_completed || [];
      const total = subModule?.sections?.length || 1;
      if (newCompleted.length >= total && !test) {
        try {
          const tRes = await api.get(`/submodules/${subModuleId}/test`);
          setTest(tRes.data);
        } catch {
          // Aún no hay test disponible para esta unidad
        }
      }
    } catch { toast.error('Error al sincronizar progreso'); }
  };

  useEffect(() => {
    if (!subModule) return;

    const fromState = location.state?.autostart;
    const fromQuery = new URLSearchParams(globalThis.location.search).get('autostart') === 'true';

    if (fromState || fromQuery) {
      const expanded = {};
      (subModule.sections || []).forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);

      setTimeout(() => {
        globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);

      globalThis.history.replaceState({}, '', globalThis.location.pathname);
    }
  }, [subModule, location.state]);

  const markUnitRead = async () => {
    setMarking(true);
    try {
      const sectionId = subModule?.sections?.[0]?.id || `unit_${subModule?.unit_number||1}_content`;
      const res = await api.post(`/submodules/${subModuleId}/mark-section-complete`, { section_id: sectionId });
      setProgress(p => ({
        ...p,
        sections_completed: res.data.sections_completed || [sectionId],
        overall_progress: res.data.overall_progress ?? 100,
      }));
      toast.success('¡Teoría procesada! Evaluación desbloqueada.');
      try {
        const tRes = await api.get(`/submodules/${subModuleId}/test`);
        setTest(tRes.data);
      } catch {
        // Aún no hay test disponible para esta unidad
      }
    } catch { toast.error('Error al sincronizar progreso'); }
    finally { setMarking(false); }
  };

  const doStartTest = async () => {
    // Seguridad: si el SCORM ya notificó aprobado y estamos navegando, no arrancar nada más
    if (scormPassedRef.current) return;

    console.log('moduleFlags al iniciar test:', moduleFlags);

    // Si el listener del SCORM ya inició la grabación, no la dupliques
    if (moduleFlags.require_screen_recording && !recording) {
      const isRecording = await startRecording();
      if (!isRecording) return;
    }

    try {
      let testData = test;
      if (!testData) {
        const r = await api.get(`/submodules/${subModuleId}/test`);
        testData = r.data;
        setTest(r.data);
      }
      setActiveTest(randomizeTest(testData));
      setTestAnswers({});
      setViolations(0);
      setShowStart(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Evaluación no disponible');
      stopRecording();
    }
  };

  const onTestPassed = (res, correct, questionCount, pct) => {
    if (res.data.module_completed) {
      const xpEarned = res.data.xp_earned ?? 0;
      toast.success(`🏆 ¡MÓDULO COMPLETADO! +${xpEarned} XP desbloqueados`);
      navigate(`/module/${moduleId}`);
      return;
    }

    toast.success(`¡Evaluación Superada! ${correct}/${questionCount} (${pct.toFixed(0)}%)`);

    const nextUnit = getNextUnit();
    if (nextUnit) {
      goToNextUnit(nextUnit);
    } else {
      goToModule({ state: { openRoleplay: true } });
    }
  };

  const submitTest = async () => {
    if (moduleFlags.require_screen_recording && !recording) {
      toast.error("⚠️ Infracción: Se ha detenido la grabación de pantalla. La evaluación no puede ser enviada.");
      return;
    }

    const missing = activeTest.questions.filter(q => testAnswers[q.id] === undefined);
    if (missing.length > 0) {
      toast.error(`Faltan ${missing.length} fragmento(s) por responder`);
      return;
    }

        setSubmitting(true);
    try {
      const threshold = test?.pass_threshold || 70;
      const questionCount = activeTest.questions.length;

      stopRecording();

      const res = await api.post(`/submodules/${subModuleId}/test-results`, {
        test_id: activeTest.id,
        answers: testAnswers,
        violations,
        screen_recorded: recording,
      });

      const { passed, percentage: pct = 0, review = [] } = res.data;
      const correct = review.filter(r => r.your_answer === r.correct).length;

      setActiveTest(null);
      setTestAnswers({});
      await load();

      if (passed) {
        onTestPassed(res, correct, questionCount, pct);
      } else {
        toast.error(`Evaluación Fallida (${pct.toFixed(0)}%) — Mínimo requerido: ${threshold}%`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la transmisión de resultados');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !minElapsed) {
    if (transitionInfo) {
      return (
        <div className="dash-root flex items-center justify-center">
          <style>{GLOBAL_STYLES}</style>
          <div
            className="text-center px-10 py-10 rounded-2xl border"
            style={{
              borderColor: P(0.4),
              boxShadow: `0 0 40px ${P(0.2)}`,
              background: 'rgba(10,8,30,0.7)',
              animation: 'fadeIn 0.5s ease',
            }}
          >
            <p className="text-[10px] font-black uppercase tracking-widest mb-3" style={{ color: cA }}>
              Evaluación superada
            </p>
            <div className="flex items-center justify-center gap-3 mb-2">
              <ArrowRight className="w-6 h-6" style={{ color: cP }} />
              <h2 className="text-3xl font-black text-white tracking-tight">
                Pasamos a la Unidad {transitionInfo.number}
              </h2>
            </div>
            {transitionInfo.title && (
              <p className="text-white/60 text-sm">{transitionInfo.title}</p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="dash-root flex flex-col items-center justify-center gap-3">
        <style>{GLOBAL_STYLES}</style>
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: cP, filter: `drop-shadow(0 0 15px ${cP})` }} />
        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: cP }}>Inicializando...</span>
      </div>
    );
  }

  if (!subModule) return (
    <div className="dash-root flex items-center justify-center">
      <style>{GLOBAL_STYLES}</style>
      <div className="glass-panel p-10 text-center" style={{ borderColor: RED(0.3) }}>
        <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3 opacity-80" />
        <p className="text-white font-bold uppercase tracking-widest">Unidad Inaccesible</p>
      </div>
    </div>
  );

  const sectionsCompleted = progress.sections_completed || [];
  const sections          = subModule.sections || [];
  const allSectionsDone   = sections.length === 0 ? sectionsCompleted.length > 0 : sectionsCompleted.length >= sections.length;
  const testPassed        = !!progress.test_passed;
  const testAttempted     = !!progress.test_attempted;
  const overallPct        = progress.overall_progress || 0;

  let evalKey = 'locked';
  if (testPassed) evalKey = 'passed';
  else if (allSectionsDone && test) evalKey = 'ready';
  const evalStatus = EVAL_STATUS[evalKey];

  // ─── VISTA TEST (Examen) ──────────────────────────────────────────────────
  if (activeTest) return (
    <div className="dash-root py-8 test-zone">
      <style>{GLOBAL_STYLES}</style>

      <div className="max-w-4xl mx-auto px-4 z-10 relative">
        <div className="mb-4 flex justify-end">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg border text-[10px] font-black uppercase tracking-widest"
            style={recording
              ? { background: RED(0.1), borderColor: RED(0.3), color: '#f87171', boxShadow: `0 0 15px ${RED(0.2)}` }
              : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
            {recording
              ? <><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" /> Monitorización Activa</>
              : <><VideoOff className="w-4 h-4" /> Sin Grabación</>}
          </div>
        </div>

        <div className="glass-panel overflow-hidden" style={{ borderColor: P(0.3), boxShadow: `0 0 40px ${P(0.12)}` }}>
          <div className="px-8 py-6 text-white relative"
            style={{ background: `linear-gradient(90deg, ${P(0.2)}, transparent)`, borderBottom: `1px solid ${P(0.2)}` }}>
            <button onClick={() => { stopRecording(); setActiveTest(null); }}
              className="absolute top-6 right-8 text-white/40 text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1 hover:text-white">
              <X className="w-4 h-4" /> Abortar
            </button>
            <p className="text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: cP }}>
              <ShieldAlert className="w-3.5 h-3.5" /> Evaluación Protocolar
            </p>
            <h2 className="text-2xl md:text-3xl font-black mb-1 tracking-tight">{subModule.title}</h2>

            <div className="flex items-center gap-4 mt-3">
              <span className="text-white/60 text-[11px] font-bold uppercase tracking-widest">{activeTest.questions.length} bloques · Umbral {test?.pass_threshold || 70}%</span>
              {test?.source === 'pdf_extracted' && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[9px] font-black tracking-widest uppercase"
                  style={{ background: A(0.1), border: `1px solid ${A(0.3)}`, color: cA }}>
                  <CheckCircle className="w-3 h-3" /> Origen SCORM Oficial
                </span>
              )}
            </div>
          </div>

          {violations > 0 && (
            <div className="mx-8 mt-6 flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 text-xs font-bold uppercase tracking-widest"
              style={{ background: RED(0.1), border: `1px solid ${RED(0.3)}`, boxShadow: `inset 0 0 15px ${RED(0.1)}` }}>
              <ShieldAlert className="w-5 h-5 text-red-500" />
              <span><strong>⚠️ {violations} INFRACCIÓN{violations !== 1 ? 'ES' : ''}</strong> DETECTADA{violations !== 1 ? 'S' : ''}</span>
            </div>
          )}

          <div className="p-8 space-y-8">
            {activeTest.questions.map((q, idx) => (
              <div key={q.id} className="bg-white/5 border border-white/10 rounded-xl p-6 relative">
                <div className="absolute top-0 right-0 bg-white/10 px-3 py-1 rounded-bl-xl rounded-tr-xl text-[10px] font-black uppercase text-white/50">{q.points} PTS</div>
                <div className="flex items-start gap-4 mb-6">
                  <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm"
                    style={{ background: P(0.2), color: cP, border: `1px solid ${P(0.4)}` }}>{idx + 1}</span>
                  <p className="font-bold text-white text-[15px] leading-snug mt-0.5">{q.question}</p>
                </div>
                <div className="space-y-3 ml-12">
                  {q.options.map((opt, oi) => {
                    const isSelected = testAnswers[q.id] === opt.id;
                    return (
                      <label key={opt.id}
  className="test-option relative flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all"
                        style={isSelected
                          ? { borderColor: cP, background: P(0.1), boxShadow: `0 0 15px ${P(0.15)}` }
                          : { borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
                        <input type="radio" name={q.id} value={opt.id} checked={isSelected}
                          onChange={() => setTestAnswers(p => ({ ...p, [q.id]: opt.id }))} className="sr-only" />
                        <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                          style={{ borderColor: isSelected ? cP : 'rgba(255,255,255,0.3)' }}>
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full" style={{ background: cP }} />}
                        </div>
                        <span className="font-black text-[13px]" style={{ color: isSelected ? cP : 'rgba(255,255,255,0.4)' }}>{String.fromCodePoint(65 + oi)}.</span>
                        <span className="text-[14px]" style={{ color: isSelected ? '#fff' : 'rgba(255,255,255,0.8)', fontWeight: isSelected ? 700 : 400 }}>{opt.text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex gap-4 pt-6 border-t border-white/10">
              <button onClick={() => { stopRecording(); setActiveTest(null); }}
                className="flex-1 px-6 py-4 border border-white/20 text-white rounded-xl hover:bg-white/10 font-bold text-xs uppercase tracking-widest transition-colors">
                Abortar Evaluación
              </button>
              <button onClick={submitTest} disabled={submitting}
                className="flex-1 btn-neon-cyan rounded-xl flex items-center justify-center gap-3 text-xs uppercase tracking-widest font-black disabled:opacity-50">
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />} Transmitir Respuestas
              </button>
            </div>
          </div>
        </div>
      </div>
      <MiniBotWidget
        title={subModule.title}
        content={subModule.content}
        sections={subModule.sections}
        isTestMode={true}
      />
    </div>
  );

  // ─── START MODAL ───────────────────────────────────────────────────────────
  if (showStart) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md dash-root">
      <style>{GLOBAL_STYLES}</style>
      <div className="bg-[#0a0a0a] rounded-2xl max-w-md w-full overflow-hidden relative z-10"
        style={{ border: `1px solid ${P(0.3)}`, boxShadow: `0 0 40px ${P(0.15)}` }}>
        <div className="px-6 py-5 text-white flex items-center gap-4"
          style={{ background: `linear-gradient(90deg, ${P(0.2)}, transparent)`, borderBottom: `1px solid ${P(0.2)}` }}>
          <div className="p-3 rounded-xl" style={{ background: P(0.2), border: `1px solid ${P(0.4)}` }}>
            <ShieldAlert className="w-6 h-6" style={{ color: cP }} />
          </div>
          <div>
            <h2 className="text-xl font-black tracking-tight">¿Iniciar Test?</h2>
            <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold mt-1">{subModule.title}</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {moduleFlags.require_screen_recording && (
            <div className="flex items-start gap-3 p-4 rounded-xl"
              style={{ background: ORANGE(0.1), border: `1px solid ${ORANGE(0.3)}`, boxShadow: `inset 0 0 15px ${ORANGE(0.05)}` }}>
              <Video className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-black text-orange-400 text-[10px] uppercase tracking-widest mb-1">Grabación requerida</p>
                <p className="text-white/60 text-xs leading-relaxed">Se solicitará compartir pantalla para supervisión.</p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 rounded-xl"
            style={{ background: P(0.1), border: `1px solid ${P(0.3)}`, boxShadow: `inset 0 0 15px ${P(0.05)}` }}>
            <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: cP }} />
            <div>
              <p className="font-black text-[10px] uppercase tracking-widest mb-1" style={{ color: cP }}>Entorno Seguro</p>
              <p className="text-white/60 text-xs leading-relaxed">Preguntas aleatorizadas. Extracción de datos bloqueada.</p>
            </div>
          </div>

          {test && (
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="text-center bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="font-black text-2xl text-white">{test.questions?.length || '?'}</p>
                <p className="text-[9px] uppercase tracking-widest font-bold text-white/40 mt-1">Preguntas</p>
              </div>
              <div className="text-center bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="font-black text-2xl" style={{ color: cA }}>{test?.pass_threshold || 70}%</p>
                <p className="text-[9px] uppercase tracking-widest font-bold mt-1" style={{ color: A(0.6) }}>Para Aprobar</p>
              </div>
              <div className="text-center bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="font-black text-2xl text-white">{(test?.max_attempts || 3) - attemptsUsed}</p>
                <p className="text-[9px] uppercase tracking-widest font-bold text-white/40 mt-1">Intentos restantes</p>
              </div>
            </div>
          )}
          {test?.source === 'pdf_extracted' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] uppercase font-black tracking-widest justify-center"
              style={{ background: A(0.1), border: `1px solid ${A(0.3)}`, color: cA }}>
              <CheckCircle className="w-4 h-4 flex-shrink-0" /> SCORM Oficial
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button onClick={() => setShowStart(false)}
            className="flex-1 px-4 py-3 border border-white/20 text-white hover:bg-white/10 rounded-xl font-bold text-xs uppercase tracking-widest transition-all">
            Cancelar
          </button>
          <button onClick={doStartTest}
            className="flex-1 btn-neon-cyan rounded-xl text-xs uppercase tracking-widest flex items-center justify-center gap-2 font-black">
            <Play className="w-4 h-4" /> Iniciar Test
          </button>
        </div>
      </div>
    </div>
  );

  // ─── BLOQUES DE LA VISTA PRINCIPAL ─────────────────────────────────────────
  const renderScorm = () => (
    <div className="px-8 pt-6 pb-4">
      <div style={{
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${P(0.2)}`,
        background: '#000',
        boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          padding: '10px 16px',
          background: P(0.06),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${P(0.12)}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookOpen className="w-4 h-4" style={{ color: cP }} />
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: P(0.7)
            }}>
              Contenido SCORM Original
            </span>
          </div>
          <button
            onClick={() => {
              const iframe = document.getElementById(`scorm-iframe-${subModule.id}`);
              if (iframe?.requestFullscreen) iframe.requestFullscreen();
            }}
            style={{
              background: P(0.1), border: `1px solid ${P(0.3)}`,
              borderRadius: 6, padding: '4px 10px', color: cP,
              fontSize: 10, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}
          >
            Pantalla completa
          </button>
        </div>
        <iframe
  ref={scormIframeRef}
  id={`scorm-iframe-${subModule.id}`}
  src={getScormSrc(subModule.scorm_url)}
  style={{ width: '100%', height: '600px', border: 'none', display: 'block' }}
  allow="fullscreen"
  title={subModule.title}
/>
      </div>
    </div>
  );

  const renderVideo = () => {
    if (!subModule.video_url) return null;
    return (
      <div className="px-8 pt-6 pb-2">
        <div style={{
          borderRadius: 16, overflow: 'hidden',
          border: `1px solid ${P(0.2)}`,
          background: '#000', boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
        }}>
          <div style={{
            padding: '10px 16px', background: P(0.06),
            display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: `1px solid ${P(0.12)}`,
          }}>
            <Video className="w-4 h-4" style={{ color: cP }} />
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: P(0.7)
            }}>Video de la unidad</span>
          </div>
          <video controls style={{ width: '100%', display: 'block', maxHeight: 480 }} preload="metadata">
            <source src={subModule.video_url} />
            {/* Subtítulos (WCAG 1.2.2): requiere que el backend devuelva captions_url con un .vtt */}
            {subModule.captions_url && (
              <track kind="captions" src={subModule.captions_url} srcLang="es" label="Español" default />
            )}
          </video>
        </div>
      </div>
    );
  };

  const renderSection = (section) => {
    const isDone = sectionsCompleted.includes(section.id);
    const isExpanded = expandedSections[section.id] !== false;
    return (
      <div key={section.id} className="bg-black/40 border border-white/10 rounded-xl overflow-hidden transition-all hover:border-white/20">
        <div className="flex items-center justify-between transition-colors"
          style={isDone ? { background: P(0.05), borderBottom: `1px solid ${P(0.2)}` } : {}}>
          <button type="button" aria-expanded={isExpanded}
            onClick={() => setExpandedSections(p => ({ ...p, [section.id]: !p[section.id] }))}
            className="flex items-center gap-4 min-w-0 flex-1 text-left px-6 py-5 cursor-pointer">
            <div className={`w-6 h-6 flex items-center justify-center rounded ${isExpanded ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'}`}>
              {isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
            </div>
            {isDone
              ? <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: cP }} />
              : <Circle className="w-5 h-5 text-white/20 flex-shrink-0" />}
            <span className="font-bold text-[15px] text-white truncate">{section.title}</span>
          </button>
          {isDone ? (
            <span className="text-[10px] uppercase tracking-widest font-black flex-shrink-0 ml-4 mr-6 hidden sm:block" style={{ color: cP }}>✓ Asimilado</span>
          ) : (
            <button onClick={() => markSectionComplete(section.id)}
              className="section-confirm flex items-center gap-1.5 px-4 py-2 bg-white/10 text-white border border-white/20 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex-shrink-0 ml-4 mr-6"
              onMouseEnter={e => { e.currentTarget.style.background = P(0.2); e.currentTarget.style.color = cP; e.currentTarget.style.borderColor = P(0.4); }}
              onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = ''; e.currentTarget.style.borderColor = ''; }}>
              <CheckCircle className="w-3.5 h-3.5" /> Confirmar
            </button>
          )}
        </div>
        {isExpanded && (
          <div className="px-6 md:px-10 py-6 border-t border-white/5 bg-black/20">
            {adapting && (
              <p className="text-[10px] uppercase tracking-widest font-black mb-3 flex items-center gap-2" style={{ color: cP }}>
                <Loader2 className="w-3 h-3 animate-spin" />Adaptando...
              </p>
            )}
            <MarkdownContent content={adaptedContent || section.content} />
          </div>
        )}
      </div>
    );
  };

  const renderSections = () => (
    <div className="p-6 md:p-8 space-y-4">
      {sections.map(renderSection)}
    </div>
  );

  const renderPlainContent = () => (
    <div className="px-8 py-10">
      {adapting && (
        <div className="flex items-center gap-3 mb-6 text-[11px] uppercase tracking-widest font-black" style={{ color: cP }}>
          <Loader2 className="w-4 h-4 animate-spin" /> La IA está adaptando el contenido...
        </div>
      )}
      <MarkdownContent content={adaptedContent || subModule.content} />
    </div>
  );

  // SCORM iframe — si existe, es el contenido principal; si no, vídeo + secciones o markdown
  const renderMainContent = () => {
    if (subModule.scorm_url) return renderScorm();
    return (
      <>
        {renderVideo()}
        {sections.length > 0 ? renderSections() : renderPlainContent()}
      </>
    );
  };

  // Acción de evaluación del footer: sin test / superada / iniciar-reintentar / sin intentos
  const renderTestAction = () => {
    if (!test) {
      return <span className="text-[10px] uppercase font-bold text-white/30 tracking-widest">Sin evaluación para esta unidad</span>;
    }
    if (testPassed) {
      return (
        <span className="flex items-center gap-2 font-black uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg"
          style={{ color: cA, background: A(0.1), border: `1px solid ${A(0.3)}` }}>
          <Award className="w-4 h-4" /> Evaluación Superada
        </span>
      );
    }

    const maxAttempts = test.max_attempts || 3;
    const remaining = maxAttempts - attemptsUsed;
    if (remaining <= 0) {
      return (
        <span className="flex items-center gap-2 text-red-400 font-black uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg"
          style={{ background: RED(0.1), border: `1px solid ${RED(0.3)}` }}>
          <Lock className="w-4 h-4" /> Sin intentos restantes ({maxAttempts}/{maxAttempts})
        </span>
      );
    }
    return (
      <button onClick={() => setShowStart(true)}
        className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 btn-neon-cyan rounded-xl text-xs uppercase tracking-widest">
        <FileText className="w-4 h-4" /> {testAttempted ? `Reintentar (${remaining} intentos restantes)` : 'Iniciar Evaluación'}
      </button>
    );
  };

  const renderFooterContent = () => {
    if (allSectionsDone) {
      return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="flex items-center gap-2 font-black uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg"
            style={{ color: cP, background: P(0.1), border: `1px solid ${P(0.3)}` }}>
            <CheckCircle className="w-4 h-4" /> Lectura de base completada
          </span>
          {renderTestAction()}
        </div>
      );
    }
    if (sections.length === 0) {
      return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[11px] font-bold text-white/50 uppercase tracking-widest">Procesa la teoría para desbloquear la evaluación.</p>
          <button onClick={markUnitRead} disabled={marking}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 btn-neon-cyan rounded-xl text-xs uppercase tracking-widest disabled:opacity-50">
            {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Confirmar Asimilación
          </button>
        </div>
      );
    }
    return <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest text-center">Confirma la asimilación de todos los bloques para acceder al test.</p>;
  };

  // ─── VISTA PRINCIPAL (Contenido) ───────────────────────────────────────────
  return (
    <div className="dash-root">
      <style>{GLOBAL_STYLES}</style>

      {/* HEADER DE UNIDAD */}
      <div className="relative z-10 bg-black/40 backdrop-blur-md" style={{ borderBottom: `1px solid ${P(0.2)}` }}>
        <div className="max-w-5xl mx-auto px-6 py-8">
          <button onClick={() => navigate(`/module/${moduleId}`)}
            className="mb-6 text-[10px] uppercase font-black tracking-widest flex items-center gap-2 transition-colors hover:text-white"
            style={{ color: cP }}>
            <ChevronLeft className="w-4 h-4" /> Nivel Superior: {parentModule?.title || 'Módulo'}
          </button>

          <div className="flex flex-col md:flex-row items-start justify-between gap-6">
            <div>
              <p className="text-[11px] text-white/50 font-black uppercase tracking-widest mb-1">Unidad {subModule.unit_number}</p>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white"
                style={{ fontFamily: 'Roberta, serif', textShadow: `0 0 15px ${P(0.4)}` }}>
                {subModule.title}
              </h1>
            </div>

            <div className="flex flex-col items-end gap-3 min-w-[200px]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest border"
                style={toneStyle(cfg.tone)}>
                <StyleIcon className="w-3.5 h-3.5" /> Adaptado: {cfg.label}
              </div>
              <div className="w-full mt-2">
                <div className="flex justify-between text-[10px] font-black uppercase tracking-widest mb-1.5" style={{ color: cA }}>
                  <span>Asimilación</span>
                  <span>{Math.round(overallPct)}%</span>
                </div>
                <div className="bg-white/10 rounded-full h-1.5 overflow-hidden border border-white/5">
                  <div className="h-full transition-all duration-500"
                    style={{ width: `${overallPct}%`, background: cA, boxShadow: `0 0 10px ${cA}` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-widest font-black border transition-all"
              style={allSectionsDone
                ? { background: P(0.1), color: cP, borderColor: P(0.3), boxShadow: `0 0 10px ${P(0.1)}` }
                : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.1)' }}>
              {allSectionsDone ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} Base de Conocimiento
            </span>
            <span className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-widest font-black border transition-all"
              style={evalStatus.style}>
              {testPassed ? <CheckCircle className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
              {evalStatus.label}
              {!allSectionsDone && <Lock className="w-3 h-3 ml-1 opacity-50" />}
            </span>
          </div>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="main-content max-w-5xl space-y-6">

        <div className="glass-panel overflow-hidden glow-cyan">

          {/* TTS BAR */}
          {learningStyle === 'auditory' && subModule.content && (
            <div className="px-8 pt-8 pb-2">
              <TTSBar content={subModule.content} />
            </div>
          )}

          {renderMainContent()}

          {/* FOOTER DEL PANEL — oculto si hay SCORM (tiene su propio test interno) */}
          {(!subModule?.scorm_url || parentModule?.use_platform_test) && (
            <div className="px-8 py-6 border-t border-white/10 bg-black/60">
              {renderFooterContent()}
            </div>
          )}
        </div>
      </div>
      <MiniBotWidget
        title={subModule.title}
        content={subModule.content}
        sections={subModule.sections}
        isTestMode={false}
      />
    </div>
  );
};

export default SubModuleViewer;